import { Card, Loader } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import type { FC } from "react";

import type { DashboardContentProps } from "@pages/Dashboard/DashboardContent/DashboardContent.types";
import { useGetConsumptionGraph } from "@services/apiEcms/useGetConsumptionGraph";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./DashboardContent.module.css";
import { DashboardFeedback } from "../DashboardFeedback/DashboardFeedback";
import { DashboardGraph } from "../DashboardGraph/DashboardGraph";

export const DashboardContent: FC<DashboardContentProps> = ({ energyType }) => {
  const { isLoading, data, error, isError } =
    useGetConsumptionGraph(energyType);
  const { t } = useCustomTranslation(["main", "common"]);
  if (isLoading) {
    return (
      <div className={classes.container}>
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={classes.container}>
        <DashboardFeedback energyType={energyType} error={error} />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Card className={classes.card}>
        <h2 className="font-title-sm">
          {t(`dashboard.consumptionGrowthCard.${energyType}`)}
        </h2>
        <div className={classes.content} data-testid="DashboardPage-cartouche">
          <h3
            className={classNames(classes.consumptionHeading, "font-title-xl")}
          >
            {data.currentYearConsumptionInKwh
              ? t("numberWithUnit", {
                  ns: "common",
                  value: data.currentYearConsumptionInKwh,
                  measurementUnit: "kWh",
                })
              : t("unavailable", { ns: "common" })}
            <span className="font-body-default">
              {t("dashboard.consumptionGrowthCard.consumptionBeginningDate", {
                date: new Date(`${data.year}-01-01`),
              })}
            </span>
          </h3>
          <p
            className={
              data.previousYearConsumption.currentYearGrowthRatio < 0
                ? classes.success
                : classes.error
            }
            data-testid="cartouche-evolution"
          >
            {t("dashboard.consumptionGrowthCard.growthRatioForPeriod", {
              value: data.previousYearConsumption.currentYearGrowthRatio,
              year: data.year - 1,
              sign:
                data.previousYearConsumption?.currentYearGrowthRatio > 0
                  ? "+"
                  : "",
            })}
          </p>
        </div>
      </Card>
      <DashboardGraph biggestValue={data?.biggestValue} data={data.data} />
    </>
  );
};
